import React, { useState, useEffect } from 'react'
import { Monitor, Smartphone, Globe, Copy } from 'lucide-react'
import { useApi } from '../../hooks/useApi'
import StatusBadge from '../../components/StatusBadge'
import Pagination from '../../components/admin/Pagination'

const Redemptions = () => {
  const [redemptions, setRedemptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState(null)
  const [filters, setFilters] = useState({
    status: '',
    product_id: '',
    search: '',
    page: 1,
    limit: 20,
  })
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [products, setProducts] = useState([])
  const [searchInput, setSearchInput] = useState('')
  const api = useApi()

  const fetchRedemptions = async () => {
    setLoading(true)
    try {
      const params = {}
      if (filters.status) params.status = filters.status
      if (filters.product_id) params.product_id = filters.product_id
      if (filters.search) params.search = filters.search
      params.page = filters.page
      params.limit = filters.limit

      const data = await api.getRedemptions(params)
      setRedemptions(data.redemptions || [])
      setTotalPages(data.total_pages || 1)
      setTotalCount(data.total || 0)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    api.getAdminProducts().then(d => setProducts(d.products || [])).catch(() => {})
  }, [])

  useEffect(() => { fetchRedemptions() }, [filters])

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(f => ({ ...f, search: searchInput, page: 1 }))
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const formatDate = (d) => {
    if (!d) return '—'
    return new Date(d).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
  }

  const timeAgo = (d) => {
    if (!d) return '—'
    const diff = Date.now() - new Date(d).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ago`
    return `${Math.floor(hours / 24)}d ago`
  }

  const getDeviceIcon = (type) => {
    if (!type) return <Monitor size={16} className="text-text-muted" />
    if (type.includes('mobile') || type.includes('tablet')) {
      return <Smartphone size={16} className="text-text-muted" />
    }
    return <Monitor size={16} className="text-text-muted" />
  }

  const getLocationIcon = () => {
    return <Globe size={16} className="text-text-muted" />
  }

  const getFlag = (country) => {
    // Simple country name to flag mapping for common countries
    const flags = {
      'Bangladesh': '🇧🇩', 'United States': '🇺🇸', 'Germany': '🇩🇪',
      'United Kingdom': '🇬🇧', 'Finland': '🇫🇮', 'India': '🇮🇳',
      'Netherlands': '🇳🇱', 'France': '🇫🇷', 'Russia': '🇷🇺',
      'Turkey': '🇹🇷', 'Pakistan': '🇵🇰', 'Private Network': '🏠',
    }
    return flags[country] || '🌍'
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <h1 className="text-2xl font-bold text-text-primary">Redemptions</h1>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <select
            value={filters.product_id}
            onChange={(e) => setFilters(f => ({ ...f, product_id: e.target.value, page: 1 }))}
            className="input"
          >
            <option value="">All Products</option>
            {products.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <select
            value={filters.status}
            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}
            className="input"
          >
            <option value="">All Statuses</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>

          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search username or code..."
            className="input"
          />
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Code</th>
                    <th>User</th>
                    <th>Status</th>
                    <th>Location</th>
                    <th>Device</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-text-muted">
                        No redemptions found
                      </td>
                    </tr>
                  ) : (
                    redemptions.map((r) => (
                      <React.Fragment key={r.id}>
                        <tr
                          onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                          className="cursor-pointer hover:bg-accent/5"
                        >
                          <td>
                            <div className="text-text-primary text-sm font-medium">{r.product_name}</div>
                            <div className="text-text-muted text-xs">{r.denomination}</div>
                          </td>
                          <td>
                            <button
                              onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(r.code || r.code_masked); }}
                              className="font-mono text-accent text-xs hover:text-accent-dark flex items-center space-x-1 group"
                              title="Click to copy"
                            >
                              <span>{r.code_masked || '—'}</span>
                              <Copy size={10} className="opacity-0 group-hover:opacity-100 flex-shrink-0" />
                            </button>
                          </td>
                          <td className="text-text-primary text-sm">{r.user_input || '—'}</td>
                          <td><StatusBadge status={r.status} size="xs" /></td>
                          <td>
                            <div className="flex items-center space-x-2">
                              {getLocationIcon()}
                              <div>
                                <div className="text-text-primary text-xs flex items-center space-x-1">
                                  <span>{getFlag(r.country)}</span>
                                  <span>{r.country || '—'}</span>
                                </div>
                                {r.city && <div className="text-text-muted text-xs">{r.city}</div>}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="flex items-center space-x-2">
                              {getDeviceIcon(r.device_type)}
                              <div>
                                <div className="text-text-primary text-xs">{r.browser || '—'}</div>
                                <div className="text-text-muted text-xs">{r.os || '—'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="text-text-muted text-xs whitespace-nowrap">{timeAgo(r.redeemed_at)}</td>
                        </tr>

                        {expandedId === r.id && (
                          <tr>
                            <td colSpan={7} className="bg-background/50 p-4">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <div className="text-text-muted text-xs mb-1">IP Address</div>
                                  <div className="text-text-primary font-mono text-xs">{r.ip_address || '—'}</div>
                                </div>
                                <div>
                                  <div className="text-text-muted text-xs mb-1">Device Type</div>
                                  <div className="text-text-primary">{r.device_type || '—'}</div>
                                </div>
                                <div>
                                  <div className="text-text-muted text-xs mb-1">Browser</div>
                                  <div className="text-text-primary">{r.browser || '—'}</div>
                                </div>
                                <div>
                                  <div className="text-text-muted text-xs mb-1">OS</div>
                                  <div className="text-text-primary">{r.os || '—'}</div>
                                </div>
                                <div>
                                  <div className="text-text-muted text-xs mb-1">G2Bulk Order ID</div>
                                  <div className="text-text-primary font-mono text-xs">{r.g2bulk_order_id || '—'}</div>
                                </div>
                                <div>
                                  <div className="text-text-muted text-xs mb-1">G2Bulk Status</div>
                                  <div>{r.g2bulk_order_status ? <StatusBadge status={r.g2bulk_order_status} size="xs" /> : '—'}</div>
                                </div>
                                <div>
                                  <div className="text-text-muted text-xs mb-1">Redeemed At</div>
                                  <div className="text-text-primary text-xs">{formatDate(r.redeemed_at)}</div>
                                </div>
                                <div>
                                  <div className="text-text-muted text-xs mb-1">Completed At</div>
                                  <div className="text-text-primary text-xs">{formatDate(r.completed_at)}</div>
                                </div>
                                {r.error_message && (
                                  <div className="col-span-full">
                                    <div className="text-text-muted text-xs mb-1">Error</div>
                                    <div className="text-error text-xs bg-error/10 border border-error/20 rounded px-2 py-1">{r.error_message}</div>
                                  </div>
                                )}
                                <div className="col-span-full">
                                  <div className="text-text-muted text-xs mb-1">Full Code</div>
                                  <div className="text-accent font-mono text-xs">{r.code}</div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              page={filters.page}
              totalPages={totalPages}
              total={totalCount}
              limit={filters.limit}
              onPageChange={(p) => setFilters(f => ({ ...f, page: p }))}
              onLimitChange={(l) => setFilters(f => ({ ...f, limit: l, page: 1 }))}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default Redemptions